import * as XLSX from 'xlsx';
import { useToast } from 'primevue/usetoast';

export function useFileParser() {
  const toast = useToast();
  const parsedRows = ref<Record<string, string>[]>([]);
  const csvColumns = ref<string[]>([]);
  const fileName = ref('');
  const isParsing = ref(false);

  function parseCsvRow(line: string): string[] {
    const cells: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') { current += '"'; i++; }
        else if (ch === '"') { inQuotes = false; }
        else { current += ch; }
      } else {
        if (ch === '"') { inQuotes = true; }
        else if (ch === ',') { cells.push(current); current = ''; }
        else { current += ch; }
      }
    }
    cells.push(current);
    return cells;
  }

  function processRows(rows: string[][], onParsed?: (columns: string[], dataRows: Record<string, string>[]) => void) {
    if (rows.length < 2) {
      toast.add({ severity: 'error', summary: 'Error', detail: 'File is empty or has no data rows.', life: 3000 });
      return;
    }

    const rawHeaders = rows[0] ?? [];
    const columns = rawHeaders.map((h) => String(h).trim()).filter((h) => h !== '');
    csvColumns.value = columns;

    const dataRows = rows.slice(1).map((row) => {
      const rowData: Record<string, string> = {};
      row.forEach((cell, i) => {
        const col = columns[i];
        if (col) rowData[col] = String(cell ?? '').trim();
      });
      return rowData;
    });
    
    parsedRows.value = dataRows;

    if (onParsed) {
      onParsed(columns, dataRows);
    }
  }

  function parseFile(file: File, onParsed?: (columns: string[], dataRows: Record<string, string>[]) => void) {
    fileName.value = file.name;
    isParsing.value = true;
    const ext = file.name.split('.').pop()?.toLowerCase();
    
    if (ext === 'csv') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const rows = text.split(/\r?\n/).map(parseCsvRow).filter((r) => r.some((c) => c.trim() !== ''));
        processRows(rows, onParsed);
        isParsing.value = false;
      };
      reader.onerror = () => {
        toast.add({ severity: 'error', summary: 'Error', detail: 'Could not read CSV file.', life: 4000 });
        isParsing.value = false;
      };
      reader.readAsText(file);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const wb = XLSX.read(data, { type: 'array' });
          const sheetName = wb.SheetNames[0];
          if (!sheetName) throw new Error('No sheets found');
          const rows: string[][] = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, defval: '' });
          processRows(rows.filter((r) => r.some((c) => String(c).trim() !== '')), onParsed);
        } catch {
          toast.add({ severity: 'error', summary: 'Error', detail: 'Could not read file.', life: 4000 });
        } finally {
          isParsing.value = false;
        }
      };
      reader.onerror = () => {
        toast.add({ severity: 'error', summary: 'Error', detail: 'Could not read Excel file.', life: 4000 });
        isParsing.value = false;
      };
      reader.readAsArrayBuffer(file);
    }
  }

  function resetParser() {
    fileName.value = '';
    parsedRows.value = [];
    csvColumns.value = [];
  }

  return {
    parseFile,
    parsedRows,
    csvColumns,
    fileName,
    isParsing,
    resetParser,
  };
}
